/*
 * This code is the intellectual property of Bulb Inc. and other software publishers;
 * it may not be altered, copied or disclosed without prior written approval.
 * Copyright 2022, Bulb Inc.  For more information, please email contact@hellobulb.com.
 */

import Cookies from 'js-cookie';
import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { forkJoin, interval, Observable } from 'rxjs';
import { switchMap, skipWhile } from 'rxjs/operators';
import { CompanionUploadRequest, CompanionUploadResponse } from './types';

/**
 * API service for Uppy component.
 */
@Injectable({
  providedIn: 'root',
})
export class AssetUploadHttpService {
  constructor(
    private http: HttpClient,
  ) { }

  /**
   * API call to create a new asset.
   *
   * @param request from the CompanionUploadRequest interface.
   */
  companionUpload(request: CompanionUploadRequest): Observable<CompanionUploadResponse> {
    return this.http
      .post(
        'assetlibrary/companion/process',
        { ...request },
      ) as Observable<CompanionUploadResponse>;
  }

  /**
   * Method that fetches the indicated assetLibrary asset, polling until the asset is done processing unless the
   * polling is disabled via the acceptProcessing option.
   * @param id the id of the asset to be fetched from the asset library
   * @param acceptProcessing whether the caller can accept a response while the asset is still processing. If not, the
   * method will continue to poll until the processing is complete, and then return the result to the observer.
   * Default is false.
   * @returns an observable that every 2 seconds makes a fetch of the asset to check its status
   * The interval will only actually emit a value under the condition that the value has an asset
   * entry with a status of COMPLETE from the server. This interval will also only emit 1 value before
   * completing. SwitchMap flattens the interval observable and the http fetch into a single stream that only
   * cares about the current emitted value and forgets about any previous values.
   */
  fetchAssetLibraryAsset(id: string, acceptProcessing: boolean = false) {
    return interval(2000)
      .pipe(
        switchMap(() => this.http.get(
          `assetlibrary/${id}`,
        ) as Observable<CompanionUploadResponse>),
        skipWhile(
          (value: CompanionUploadResponse) => (!acceptProcessing && value.entry.status === 'PROCESSING'),
        ),
      );
  }

  /**
   * Method that fetches the indicated assetLibrary assets, polling until the assets are done processing unless the
   * polling is disabled via the acceptProcessing option.
   * @param ids the id of the assets to be fetched from the asset library
   * @param acceptProcessing whether the caller can accept a response while the asset is still processing. If not, the
   * method will continue to poll until the processing is complete, and then return the result to the observer.
   * Default is false.
   * @returns an observable that every 2 seconds makes a fetch of the asset to check its status
   * The interval will only actually emit a value under the condition that the value has an asset
   * entry with a status of COMPLETE from the server. This interval will also only emit 1 value before
   * completing. SwitchMap flattens the interval observable and the http fetch into a single stream that only
   * cares about the current emitted value and forgets about any previous values.
   */
  fetchAllAssetLibraryAsset(ids: string[], acceptProcessing: boolean = false) {
    return interval(2000).pipe(
      switchMap(() => forkJoin(
        ids.map((id) => this.http.get(`assetlibrary/${id}`) as Observable<CompanionUploadResponse>),
      )),
      skipWhile((responses: CompanionUploadResponse[]) => responses.some(
        (response) => !acceptProcessing && response.entry.status === 'PROCESSING',
      )),
    );
  }
}
